import type {
  AdjustDoseRequest,
  AdminClosableWorkItemKind,
  AdminCloseWorkItemRequest,
  CoachFollowUp,
  CreateCoachFollowUpRequest,
  DisposeWorkItemRequest,
  FollowUpPolicyDto,
  LabResultCheckOutcome,
  LabResultCheckResult,
  SetFollowUpPolicyInput,
  WorkQueueItem,
  WorkQueueItemContext,
  WorkQueueItemKind,
  ApproveRenewalRequest,
  FollowUpRequest,
  IssuePrescriptionRequest,
  MarkLabNotNeededRequest,
  MonthlyReviewDecisions,
  MonthlyReviewJournal,
  PublishMonthlyReviewInput,
  RejectRenewalRequest,
  RequestCycleCompletion,
  SetNextFollowUp,
  UpdateJournalSections,
  WorkQueue,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-289 / TES-290 — admin client for the clinician work-queue workspace + the
// doctor decision-loop actions. Mirrors the existing thin-wrapper pattern: cookie
// auth via `withCredentials`, tenant header, structured-error unwrap via jsonOr401.

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

export async function fetchWorkQueue(): Promise<WorkQueue> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/work-queue`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/${path}`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOr401(res);
}

const cyclePath = (patientId: string, cycleId: string) =>
  `patients/${patientId}/cycles/${cycleId}`;

export function approveRenewal(
  patientId: string,
  cycleId: string,
  body: ApproveRenewalRequest,
) {
  return post(`${cyclePath(patientId, cycleId)}/renewal/approve`, body);
}

// TES-997 (Ö-259) — "Nytt recept är utfärdat i Webdoc", recorded after a
// decision that published without an issued prescription. TES-1055 — with
// the Webdoc journal confirmation, which the API requires.
export function issuePrescription(
  patientId: string,
  cycleId: string,
  body: IssuePrescriptionRequest,
) {
  return post(`${cyclePath(patientId, cycleId)}/prescription/issued`, body);
}

// TES-904 step 4 / TES-978 — the monthly review's journal note, its own ten
// headings. The first read creates the boxes and, while the review is open,
// prefills them from her check-ins, readings and the cycle.
export async function fetchMonthlyReviewJournal(
  patientId: string,
  cycleId: string,
): Promise<MonthlyReviewJournal> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/${cyclePath(patientId, cycleId)}/journal`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function saveMonthlyReviewJournal(
  patientId: string,
  cycleId: string,
  input: UpdateJournalSections,
): Promise<MonthlyReviewJournal> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/${cyclePath(patientId, cycleId)}/journal`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

// TES-1034 — the shorthand ("Stödord"), "Sammanställ journal" and the
// per-version receipt are gone from the monthly review, as from the care
// meeting in TES-1019: the note is written under its headings, and its final
// version and Webdoc receipt are stored by the decision itself
// (`webdocControl` on approve, reject and the feedback publish). The API
// routes stay for history.

// TES-978 — the monthly review's decisions beside the renewal: the follow-up
// ("Nästa uppföljning") and the komplettering.
export async function fetchMonthlyReviewDecisions(
  patientId: string,
  cycleId: string,
): Promise<MonthlyReviewDecisions> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/${cyclePath(patientId, cycleId)}/decisions`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

export async function setNextFollowUp(
  patientId: string,
  cycleId: string,
  body: SetNextFollowUp,
): Promise<MonthlyReviewDecisions> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/${cyclePath(patientId, cycleId)}/next-follow-up`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(body),
      ...withCredentials,
    },
  );
  return jsonOr401(res);
}

export function requestCycleCompletion(
  patientId: string,
  cycleId: string,
  body: RequestCycleCompletion,
): Promise<MonthlyReviewDecisions> {
  return post(`${cyclePath(patientId, cycleId)}/completion`, body);
}

export function rejectRenewal(
  patientId: string,
  cycleId: string,
  body: RejectRenewalRequest,
) {
  return post(`${cyclePath(patientId, cycleId)}/renewal/reject`, body);
}

export function adjustDose(
  patientId: string,
  cycleId: string,
  body: AdjustDoseRequest,
) {
  return post(`${cyclePath(patientId, cycleId)}/dose`, body);
}

// TES-1034 — the free-standing "Klinisk anteckning" (POST …/note) is no
// longer written from the admin: everything the doctor writes goes under its
// journal heading. The route stays for history.

// CP-33 — publish the doctor's patient-facing monthly feedback (write + gate
// in the same action; republishing overwrites the text).
// TES-1011 — `clinicalPhase` is the review's phase decision, sent only when
// the doctor picked one. TES-1034 — `webdocControl` and `journalOrder`: the
// journal note confirmed in Webdoc, as on every monthly review decision.
export function publishReviewFeedback(
  patientId: string,
  cycleId: string,
  body: PublishMonthlyReviewInput,
) {
  return post(`${cyclePath(patientId, cycleId)}/review-feedback`, body);
}

export function markLabNotNeeded(
  patientId: string,
  cycleId: string,
  body: MarkLabNotNeededRequest,
) {
  return post(`${cyclePath(patientId, cycleId)}/lab-not-needed`, body);
}

export function requestFollowUp(patientId: string, body: FollowUpRequest) {
  return post(`patients/${patientId}/follow-up`, body);
}

// ---------------------------------------------------------------------------
// TES-757 — the expanded card (API landed in #725)
// ---------------------------------------------------------------------------

// Fetched when the card opens, never folded into the list response: the queue
// already carries PHI and the contract's own header warns that anything added
// has to be weighed as clinical data leaving its owning surface.
export async function fetchWorkItemContext(
  kind: WorkQueueItemKind,
  itemId: string,
): Promise<WorkQueueItemContext> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/work-queue/items/${kind}/${encodeURIComponent(itemId)}`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

// Records what the clinician did with the row and hands back the NEXT item, so
// she never has to return to the list. See the model comment in schema.prisma:
// most closes happen in the source, not here.
export async function disposeWorkItem(
  kind: WorkQueueItemKind,
  itemId: string,
  body: DisposeWorkItemRequest,
): Promise<{ next: WorkQueueItem | null }> {
  return post(
    `work-queue/items/${kind}/${encodeURIComponent(itemId)}/dispose`,
    body,
  );
}

// TES-751 — pause new reminders / record an agreed contact frequency.
// Patient-scoped, not item-scoped: the policy outlives any single card.
export async function setFollowUpPolicy(
  patientId: string,
  body: SetFollowUpPolicyInput,
): Promise<FollowUpPolicyDto> {
  return post(
    `work-queue/items/follow-up-policy/${encodeURIComponent(patientId)}`,
    body,
  );
}

// TES-765 (CP-05) — "Infört i Webdoc". The receipt that silences both the
// 24-hour email and the 48-hour row in the doctor's queue.
export async function confirmWebdocEntry(
  patientId: string,
  meetingId: string,
): Promise<{ webdocConfirmedAt: string }> {
  return post(
    `patients/${encodeURIComponent(patientId)}/meetings/${encodeURIComponent(
      meetingId,
    )}/webdoc-confirmation`,
    {},
  );
}

// TES-1284 — "Kontrollera provsvar i Webdoc": the doctor's answer, one of
// "Provsvar finns", "Provsvar har inte kommit ännu", "Problem med remiss
// eller provtagning". Doctor only on the server.
export async function recordLabResultCheck(
  patientId: string,
  orderId: string,
  outcome: LabResultCheckOutcome,
): Promise<LabResultCheckResult> {
  return post(
    `patients/${encodeURIComponent(patientId)}/lab-orders/${encodeURIComponent(
      orderId,
    )}/result-check`,
    { outcome },
  );
}

// TES-1400 (Ö-410) — "Stäng arbetsmoment": the admin closes an
// administrative work item with a reason. Admin only on the server, and only
// for ADMIN_CLOSABLE_WORK_ITEM_KINDS.
export async function adminCloseWorkItem(
  kind: AdminClosableWorkItemKind,
  itemId: string,
  body: AdminCloseWorkItemRequest,
): Promise<{ closed: true }> {
  return post(
    `work-queue/items/${kind}/${encodeURIComponent(itemId)}/admin-close`,
    body,
  );
}

export async function createCoachFollowUp(
  patientId: string,
  body: CreateCoachFollowUpRequest,
): Promise<CoachFollowUp> {
  return post(`patients/${patientId}/follow-ups`, body);
}
